import dayjs from 'dayjs';

export interface BadgeData {
  visitorType: string;
  visitorName: string;
  visitorCompany: string;
  hostLabel: string;
  hostName: string;
  orgName: string;
  visitDate: string;
}

export async function printBadge(badgeData: BadgeData): Promise<void> {
  const normalizedVisitorType = (
    badgeData.visitorType || ''
  ).toLocaleUpperCase();

  const templateUrls = [
    '/visitor-badge.html',
    new URL('../public/visitor-badge.html', import.meta.url).toString(),
  ];

  const templateCandidates = await Promise.all(
    templateUrls.map(async (url) => {
      try {
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) return null;

        const html = await response.text();
        return html.includes('VISITOR FULL NAME') ? html : null;
      } catch {
        return null;
      }
    }),
  );

  const templateHtml = templateCandidates.find(
    (candidate): candidate is string => Boolean(candidate),
  );

  if (!templateHtml) {
    throw new Error('Failed to load badge template');
  }

  const renderedTemplateHtml = templateHtml
    .replaceAll('VISITOR TYPE', normalizedVisitorType)
    .replaceAll('VISITOR FULL NAME', badgeData.visitorName)
    .replaceAll('Visitor Company', badgeData.visitorCompany)
    .replaceAll('HOST LABEL', badgeData.hostLabel)
    .replaceAll('HOST NAME', badgeData.hostName)
    .replaceAll('Org Name', badgeData.orgName)
    .replaceAll('YYYY-MM-DD', badgeData.visitDate);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = '0';
  iframe.setAttribute('title', 'Badge Print');

  const cleanup = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  };

  iframe.onload = () => {
    const contentWindow = iframe.contentWindow;
    if (!contentWindow) {
      cleanup();
      return;
    }

    const handleAfterPrint = () => {
      contentWindow.removeEventListener('afterprint', handleAfterPrint);
      cleanup();
    };

    contentWindow.addEventListener('afterprint', handleAfterPrint);
    contentWindow.focus();

    setTimeout(() => {
      contentWindow.print();
    }, 150);

    setTimeout(cleanup, 30_000);
  };

  document.body.appendChild(iframe);
  iframe.srcdoc = renderedTemplateHtml;
}

export function formatVisitorName(
  firstName: string | null,
  middleName: string | null,
  lastName: string | null,
): string {
  const capitalizeFirst = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';
    return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
  };

  const parts = [firstName, middleName, lastName]
    .filter(Boolean)
    .map((part) => capitalizeFirst(part as string))
    .filter(Boolean);

  return parts.join(' ') || 'Unknown Visitor';
}

export function formatVisitDate(dateString: string | null): string {
  if (!dateString) return new Date().toISOString().split('T')[0];
  try {
    return dayjs(dateString).format('YYYY-MM-DD');
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}
